/**
 * Feature flag gating native video attachments in chat (default off). Declared
 * here — the one place both the agent-studio composer (frontend) and the API
 * engine (backend) import — so the flag name can't drift between the two gates.
 */
export const CHAT_VIDEO_ATTACHMENTS_FLAG = 'chat_video_attachments';
export const FILE_SIZE_LIMITS = {
    LLM: {
        DEFAULT: 20 * 1024 * 1024, // 20 MB in bytes
        ANTHROPIC_SONNET: 10 * 1024 * 1024, // 10 MB in bytes
        // A clip is routinely larger than the inline limit. The original runtime sends it
        // by Gemini File API URI; Relay sends its bytes inline, up to this ceiling.
        VIDEO: 100 * 1024 * 1024, // 100 MB in bytes
    },
    ASSETS: {
        DEFAULT: 100 * 1024 * 1024, // 100 MB in bytes
        // Ad creatives, which are the one surface that uploads a video master rather than a
        // document. Separate from DEFAULT so the ceiling every other surface presigns against -
        // knowledge bases, RAG, analysis - is not widened along with it.
        VIDEO: 1024 * 1024 * 1024, // 1 GB in bytes
        // Content Studio's board export zip, which bundles every frame of a board (up to 16) as
        // full-resolution PNGs, including print-format posters (A2/A3 at ~300dpi, several
        // thousand pixels per side). Lossless PNG at that scale runs many times larger than the
        // JPG equivalent, which stays comfortably under DEFAULT. Separate from DEFAULT so the
        // ceiling every other direct upload - documents, images - checks against is not widened.
        EXPORT_ZIP: 300 * 1024 * 1024, // 300 MB in bytes
        // A single rendered export frame, presigned individually before it joins the zip above.
        // A2 at 300dpi rendered at 2x is 9922x14032, about the most a chosen export scale allows a
        // frame - raw RGBA is ~530MB, and
        // measuring actual `sharp` PNG output at that resolution puts a maximally incompressible
        // frame (near-random pixel data) at ~460MB, with realistic photographic content well
        // below that. 500MB covers the measured worst case with headroom without approaching an
        // arbitrary multi-GB ceiling. Only service-to-service callers get this - see
        // `presignCeilingFor` in assets-api - so a user-uploaded image still checks against
        // DEFAULT.
        EXPORT_FRAME: 500 * 1024 * 1024, // 500 MB in bytes
    },
};
/**
 * MIME types accepted for chat file uploads.
 *
 * Read by the chat file input, workspace-api comment attachments and project files. The
 * assets-api presign endpoint validates against {@link PRESIGNABLE_MIME_TYPES} instead,
 * which is wider - adding a type here widens all three of those surfaces.
 */
export const CHAT_UPLOAD_MIME_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'video/mp4',
    'video/quicktime',
    'video/webm',
    'video/3gpp',
    'video/ogg',
    'video/mpeg',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/markdown',
    'text/x-markdown',
    'text/plain',
];
/**
 * MIME types for font uploads: the modern webfont formats plus the legacy MIME strings older
 * browsers and OS file-type sniffing still report for a `.ttf`/`.woff` (`application/x-font-ttf`,
 * `application/font-woff`). Accepted on input, never advertised as the canonical type for a
 * format.
 */
export const FONT_MIME_TYPES = [
    'font/ttf',
    'font/otf',
    'font/woff',
    'font/woff2',
    'application/x-font-ttf',
    'application/font-woff',
];
/**
 * MIME types the assets-api presign endpoint will mint an upload target for.
 *
 * A superset of {@link CHAT_UPLOAD_MIME_TYPES}, and separate from it because the two answer
 * different questions. Media-delivery creatives include animated GIF, which every ad
 * platform takes and no chat surface offers; folding it into the chat list would also let a
 * GIF into a comment attachment and a project file, which nothing asked for. The same goes for
 * zip, which media-delivery takes as a plan bundled with its creatives and unpacks on a worker.
 */
export const PRESIGNABLE_MIME_TYPES = [
    ...CHAT_UPLOAD_MIME_TYPES,
    'image/gif',
    ...FONT_MIME_TYPES,
    'text/csv',
    'application/zip',
];
/**
 * Accepted file types for knowledge base uploads.
 * Maps MIME types to their file extensions.
 * Used by both the frontend (KB file input) and
 * the assets-api (RAG file processing).
 */
export const KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES = {
    'text/plain': ['.txt'],
    'text/markdown': ['.md'],
    'text/csv': ['.csv'],
    'text/html': ['.html'],
    'application/json': ['.json', '.campaign'],
    'application/x-jsonlines': ['.jsonl'],
    'application/pdf': ['.pdf'],
    'application/xml': ['.xml'],
    'text/xml': ['.xml'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};
/** Maps each font MIME type to its extension(s), same shape {@link KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES} uses. */
export const FONT_ACCEPTED_FILE_TYPES = {
    'font/ttf': ['.ttf'],
    'font/otf': ['.otf'],
    'font/woff': ['.woff'],
    'font/woff2': ['.woff2'],
    'application/x-font-ttf': ['.ttf'],
    'application/font-woff': ['.woff'],
};
